import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useToast } from '../hooks/useToast';
import { useAuth } from '../context/AuthContext';
import { useSendJobs } from '../context/SendJobsContext';

const api = axios.create({ baseURL: '/api', withCredentials: true });

const STATUSES = [
  { key: 'all',            label: 'All' },
  { key: 'new_lead',       label: 'New Lead',      cls: 'badge-muted' },
  { key: 'pitched',        label: 'Pitched',       cls: 'badge-blue' },
  { key: 'followed_up',    label: 'Followed Up',   cls: 'badge-purple' },
  { key: 'replied',        label: 'Replied',       cls: 'badge-amber' },
  { key: 'interested',     label: 'Interested',    cls: 'badge-amber' },
  { key: 'call_booked',    label: 'Call Booked',   cls: 'badge-green' },
  { key: 'client',         label: 'Client ✓',      cls: 'badge-green' },
  { key: 'not_interested', label: 'Not Interested', cls: 'badge-red' },
  { key: 'invalid',        label: 'Invalid',       cls: 'badge-muted' },
];

const BACKFILL_CONFIGS = {
  threads: {
    title: 'Find Missing Thread IDs',
    description: "Searches your connected ophalai.com accounts' Sent folders for the original outreach email of every lead marked as pitched but missing a Thread ID — needed for follow-ups to reply in the right thread. Doesn't send anything, just reads message metadata.",
    endpoint: 'backfill-threads',
    runningLabel: 'Searching...',
    summarize: result => result?.total === 0
      ? 'Every pitched lead already has a Thread ID.'
      : <>{result?.found} of {result?.total} found{result?.unmatched ? ` — ${result.unmatched} couldn't be matched (likely sent from an account that isn't connected here)` : ''}.</>,
  },
  followups: {
    title: 'Backfill Follow-Up Dates',
    description: "For leads that already have a Thread ID but are missing Follow Up 1/2/3 dates, reads each thread's message headers (no content) and fills in dates for the follow-ups you actually sent — but only up to the point the lead replied, if they did. Anything sent after a reply is a real conversation, not a cadence follow-up, so it's left for the AI status review instead.",
    endpoint: 'backfill-followups',
    runningLabel: 'Checking threads...',
    summarize: result => result?.total === 0
      ? 'Every lead with a Thread ID already has all its follow-up dates filled.'
      : <>{result?.updated} of {result?.total} updated{result?.hasReply ? ` — ${result.hasReply} had a reply partway through (run the AI status review for those)` : ''}{result?.skipped ? `, ${result.skipped} skipped` : ''}.</>,
  },
  status: {
    title: 'Review Replies & Classify Status',
    description: "One-time cleanup for leads that predate live status tracking. Reads the full content of each thread that has a reply from the lead (not just headers) and asks Claude to classify their current status (Interested, Call Booked, Client, Not Interested, Invalid, or Replied if genuinely ambiguous). This is the only backfill that reads real message content, not just metadata — it only runs once per lead going forward, since new replies are tracked live by the app as they happen.",
    endpoint: 'backfill-status',
    runningLabel: 'Reading threads...',
    summarize: result => result?.total === 0
      ? 'Every lead with a thread has already been reviewed.'
      : <>{result?.classified} classified, {result?.noReply} had no reply{result?.skipped ? `, ${result.skipped} skipped` : ''} (of {result?.total} checked).</>,
  },
};

function LogBox({ logs }) {
  const ref = useRef(null);
  useEffect(() => { if (ref.current) ref.current.scrollTop = ref.current.scrollHeight; }, [logs]);
  return (
    <div ref={ref} style={{
      background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
      padding: 12, fontFamily: 'monospace', fontSize: 12, color: 'var(--text-secondary)',
      height: 200, overflowY: 'auto', marginTop: 12,
    }}>
      {logs.length === 0
        ? <span style={{ color: 'var(--text-muted)' }}>Waiting...</span>
        : logs.map((l, i) => <div key={i}>{l}</div>)}
    </div>
  );
}

// ─── Backfill Modal (Thread IDs / Follow-Up Dates / AI Status Review) ──────
// One shared shell for all three one-time cleanup jobs — they only differ in
// endpoint, copy, and how the final result count is summarized.
function BackfillModal({ title, description, endpoint, runningLabel, summarize, onClose, onDone }) {
  const [phase, setPhase] = useState('idle'); // idle | running | done | error
  const [logs, setLogs] = useState([]);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const pollRef = useRef(null);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const poll = (jobId) => {
    pollRef.current = setInterval(async () => {
      try {
        const res = await api.get(`/leads/${endpoint}/status/${jobId}`);
        const job = res.data;
        setLogs(job.logs || []);
        if (job.status === 'done') {
          clearInterval(pollRef.current);
          setResult(job.result);
          setPhase('done');
        } else if (job.status === 'error') {
          clearInterval(pollRef.current);
          setError(job.error || 'Backfill failed.');
          setPhase('error');
        }
      } catch (_) {}
    }, 2000);
  };

  const handleStart = async () => {
    setPhase('running');
    setLogs([]);
    setError('');
    try {
      const res = await api.post(`/leads/${endpoint}`);
      poll(res.data.jobId);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
      setPhase('error');
    }
  };

  const handleFinish = () => {
    if (result?.total > 0) onDone();
    onClose();
  };

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h2>{title}</h2>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>

        {phase === 'idle' && (
          <div>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 20 }}>
              {description}
            </p>
            <div className="flex-center gap-2" style={{ justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
              <button className="btn btn-primary" onClick={handleStart}>Start</button>
            </div>
          </div>
        )}

        {phase === 'running' && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--accent)', animation: 'pulse 1.5s infinite' }} />
              <span style={{ fontWeight: 500 }}>{runningLabel}</span>
            </div>
            <LogBox logs={logs} />
          </div>
        )}

        {phase === 'error' && (
          <div>
            <div style={{ color: 'var(--red)', fontSize: 13, background: 'var(--red-soft)', borderRadius: 'var(--radius-sm)', padding: '10px 14px', marginBottom: 16 }}>
              {error}
            </div>
            <button className="btn btn-ghost" onClick={onClose}>Close</button>
          </div>
        )}

        {phase === 'done' && (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>✓</div>
            <h3 style={{ fontSize: 16, marginBottom: 8 }}>Done!</h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 20 }}>
              {summarize(result)}
            </p>
            <button className="btn btn-primary" onClick={handleFinish}>Close</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Scan Modal ─────────────────────────────────────────────────────────────
// One flagged duplicate from a scan result, with Add anyway / Confirm skip actions.
function DuplicateReviewItem({ item, onResolved }) {
  const toast = useToast();
  const [status, setStatus] = useState('pending'); // pending | adding | added | skipped
  const { row, reason, matchedAgainst } = item;

  const handleAdd = async () => {
    setStatus('adding');
    try {
      await api.post('/leads/add-duplicate', { row });
      setStatus('added');
      onResolved();
      toast(`Added "${row.fullName}" ✓`, 'success');
    } catch (e) {
      setStatus('pending');
      toast(e.response?.data?.error || 'Could not add', 'error');
    }
  };

  const handleSkip = () => setStatus('skipped');

  return (
    <div style={{
      border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: '10px 12px',
      marginBottom: 8, opacity: status === 'skipped' ? 0.5 : 1,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
        <div>
          <div style={{ fontWeight: 500, fontSize: 13 }}>{row.fullName}</div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>from {row.show}</div>
          <div style={{ fontSize: 11, color: 'var(--amber)', marginTop: 4 }}>
            {reason === 'social' ? 'Shares a social handle with' : 'Similar name to'}{' '}
            {matchedAgainst.sheetUrl ? (
              <a href={matchedAgainst.sheetUrl} target="_blank" rel="noreferrer" style={{ color: 'var(--accent)' }}>
                "{matchedAgainst.fullName}" (row {matchedAgainst.rowNum} on the sheet) ↗
              </a>
            ) : (
              <>"{matchedAgainst.fullName}" — also found in this scan</>
            )}
          </div>
        </div>
        <div style={{ flexShrink: 0 }}>
          {status === 'pending' && (
            <div className="flex-center gap-1">
              <button className="btn btn-sm btn-secondary" onClick={handleSkip}>Confirm skip</button>
              <button className="btn btn-sm btn-primary" onClick={handleAdd}>Add anyway</button>
            </div>
          )}
          {status === 'adding' && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Adding...</span>}
          {status === 'added' && <span style={{ fontSize: 12, color: 'var(--green)' }}>Added ✓</span>}
          {status === 'skipped' && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Skipped</span>}
        </div>
      </div>
    </div>
  );
}

function ScanModal({ onClose, onDone, onRefresh }) {
  const toast = useToast();
  const auth = useAuth();
  const [appleUrl, setAppleUrl] = useState('');
  const [guestTarget, setGuestTarget] = useState(10);
  // With login enforced, who a scan is "for" is picked from the roster of
  // everyone who's ever logged in (see /leads/scan-owners) — both the pitch
  // text's "My name is ___" line and the lead's ownership come from this same
  // selection, so they can't drift apart. Defaults to whoever's currently
  // logged in, but assignable to anyone else in the dropdown (e.g. Daniel
  // running a scan on Aaron's behalf). Falls back to the old free-text name
  // field only when there's no login roster to pick from at all.
  const [owners, setOwners] = useState([]);
  const [ownerEmail, setOwnerEmail] = useState('');
  const [senderName, setSenderName] = useState(() => localStorage.getItem('pf_lead_sender_name') || '');
  const [running, setRunning] = useState(false);
  const [logs, setLogs] = useState([]);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [addedCount, setAddedCount] = useState(0);
  const pollRef = useRef(null);

  useEffect(() => {
    api.get('/leads/scan-owners').then(res => {
      const list = res.data.owners || [];
      setOwners(list);
      if (list.some(o => o.email === auth?.email)) setOwnerEmail(auth.email);
      else if (list.length) setOwnerEmail(list[0].email);
    }).catch(() => {});
  }, [auth?.email]);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const poll = (jobId) => {
    pollRef.current = setInterval(async () => {
      try {
        const res = await api.get(`/leads/scan/status/${jobId}`);
        const job = res.data;
        setLogs(job.logs || []);
        if (job.status === 'done') {
          clearInterval(pollRef.current);
          setRunning(false);
          setResult(job.result);
        } else if (job.status === 'error') {
          clearInterval(pollRef.current);
          setRunning(false);
          setError(job.error || 'Scan failed.');
        }
      } catch (_) {}
    }, 2000);
  };

  const hasOwnerRoster = owners.length > 0;
  const canScan = appleUrl.trim() && (hasOwnerRoster ? ownerEmail : senderName.trim());

  const handleScan = async () => {
    if (!appleUrl.trim() || (hasOwnerRoster ? !ownerEmail : !senderName.trim())) return;
    if (!hasOwnerRoster) localStorage.setItem('pf_lead_sender_name', senderName.trim());
    setRunning(true);
    setLogs([]);
    setError('');
    setResult(null);
    try {
      const body = hasOwnerRoster
        ? { appleUrl: appleUrl.trim(), guestTarget, ownerEmail }
        : { appleUrl: appleUrl.trim(), guestTarget, senderName: senderName.trim() };
      const res = await api.post('/leads/scan', body);
      poll(res.data.jobId);
    } catch (e) {
      setRunning(false);
      setError(e.response?.data?.error || e.message);
    }
  };

  const handleFinish = () => {
    if (addedCount) toast(`${addedCount} more lead${addedCount === 1 ? '' : 's'} added ✓`, 'success');
    onDone();
  };

  const duplicates = result?.duplicates || [];
  const hasDuplicates = duplicates.length > 0;

  return (
    <div className="modal-overlay">
      <div className={`modal${hasDuplicates ? ' modal-lg' : ''}`}>
        <div className="modal-header">
          <h2>Scan for New Leads</h2>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>

        {result ? (
          <div>
            <div style={{ textAlign: 'center', padding: '12px 0 20px' }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>✓</div>
              <h3 style={{ fontSize: 16, marginBottom: 6 }}>Done!</h3>
              <p style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                Found {result.totalFound} guest{result.totalFound === 1 ? '' : 's'} from "{result.podcastTitle}" —{' '}
                {result.count} added{hasDuplicates ? `, ${duplicates.length} flagged as possible duplicates` : ''}.
              </p>
            </div>

            {hasDuplicates && (
              <div style={{ marginBottom: 16 }}>
                <label style={{ marginBottom: 8, display: 'block' }}>Review flagged duplicates</label>
                <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>
                  These looked like people already on the sheet. Check each one — add it back if it's actually a different person.
                </p>
                <div style={{ maxHeight: 320, overflowY: 'auto' }}>
                  {duplicates.map((item, i) => (
                    <DuplicateReviewItem
                      key={i}
                      item={item}
                      onResolved={() => { setAddedCount(c => c + 1); onRefresh(); }}
                    />
                  ))}
                </div>
              </div>
            )}

            <div className="flex-center gap-2" style={{ justifyContent: 'flex-end' }}>
              <button className="btn btn-primary" onClick={handleFinish}>Close</button>
            </div>
          </div>
        ) : error ? (
          <div>
            <div style={{ color: 'var(--red)', fontSize: 13, background: 'var(--red-soft)', borderRadius: 'var(--radius-sm)', padding: '10px 14px', marginBottom: 16 }}>
              {error}
            </div>
            <button className="btn btn-ghost" onClick={onClose}>Close</button>
          </div>
        ) : running ? (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--accent)', animation: 'pulse 1.5s infinite' }} />
              <span style={{ fontWeight: 500 }}>Scanning...</span>
            </div>
            <p style={{ color: 'var(--text-secondary)', fontSize: 13 }}>This may take a few minutes depending on how many episodes need checking.</p>
            <LogBox logs={logs} />
          </div>
        ) : (
          <>
            <div className="form-group">
              <label>Apple Podcasts URL</label>
              <input value={appleUrl} onChange={e => setAppleUrl(e.target.value)} placeholder="https://podcasts.apple.com/us/podcast/.../id123456789" autoFocus />
            </div>
            <div className="form-group">
              <label>How many guests to find?</label>
              <input type="number" min={1} value={guestTarget} onChange={e => setGuestTarget(Math.max(1, parseInt(e.target.value) || 1))} style={{ width: 100 }} />
            </div>
            <div className="form-group">
              {hasOwnerRoster ? (
                <>
                  <label>Assign to <span style={{ color: 'var(--text-muted)', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(used in the outreach pitch, and only they can pitch these leads until sent)</span></label>
                  <select value={ownerEmail} onChange={e => setOwnerEmail(e.target.value)}>
                    {owners.map(o => <option key={o.email} value={o.email}>{o.name || o.email}</option>)}
                  </select>
                </>
              ) : (
                <>
                  <label>Your Name <span style={{ color: 'var(--text-muted)', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(used in the outreach pitch)</span></label>
                  <input value={senderName} onChange={e => setSenderName(e.target.value)} placeholder="e.g. Alex" />
                </>
              )}
            </div>
            <div className="flex-center gap-2" style={{ justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
              <button className="btn btn-primary" disabled={!canScan} onClick={handleScan}>Scan</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Edit Lead Modal ────────────────────────────────────────────────────────
function EditLeadModal({ lead, onClose, onSave }) {
  const toast = useToast();
  const [email, setEmail] = useState(lead.email || '');
  const [pitchText, setPitchText] = useState(lead.pitchText || '');
  const [notes, setNotes] = useState(lead.notes || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.patch(`/leads/${lead.id}`, { email, pitchText, notes });
      onSave(res.data);
      toast('Saved ✓', 'success');
    } catch (e) {
      toast(e.response?.data?.error || 'Error saving', 'error');
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal modal-lg">
        <div className="modal-header">
          <div>
            <h2>{lead.fullName}</h2>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}>{lead.podcastName}</div>
          </div>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>
        <div className="form-group">
          <label>Email</label>
          <input value={email} onChange={e => setEmail(e.target.value)} placeholder="guest@example.com" autoFocus />
        </div>
        <div className="form-group">
          <label>Pitch</label>
          <textarea value={pitchText} onChange={e => setPitchText(e.target.value)} rows={10} />
        </div>
        <div className="form-group">
          <label>Notes</label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} />
        </div>
        <div className="flex-center gap-2" style={{ justifyContent: 'flex-end' }}>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}

// ─── Send Outreach Modal ────────────────────────────────────────────────────
function SendLeadsModal({ onClose, onSent }) {
  const toast = useToast();
  const [sendType, setSendType] = useState('initialPitch');
  const [eligible, setEligible] = useState([]);
  const [loading, setLoading] = useState(true);
  const [count, setCount] = useState(10);
  const [rowFrom, setRowFrom] = useState('');
  const [rowTo, setRowTo] = useState('');
  const [subject, setSubject] = useState('Came across your podcast interview');
  const [googleAccounts, setGoogleAccounts] = useState([]);
  const [accountEmail, setAccountEmail] = useState('');
  const [followUpFilterEmail, setFollowUpFilterEmail] = useState('');
  const [jobId, setJobId] = useState(null);
  // The send job lives in the shared SendJobsContext (same as podcast
  // outreach), so closing this modal mid-send just minimizes it to the
  // floating progress card instead of losing track of it.
  const { jobs, addJob, cancelJob: ctxCancelJob, dismissJob } = useSendJobs();
  const ctxJob = jobs.find(j => j.jobId === jobId);
  // cache last known job state so modal keeps showing "Done" even after context auto-dismisses
  const lastJobRef = useRef(null);
  if (ctxJob) lastJobRef.current = ctxJob;
  const job = ctxJob || (jobId ? lastJobRef.current : null);

  const TYPES = [
    { key: 'initialPitch', label: 'New Outreach', desc: 'Not yet pitched' },
    { key: 'followUp1',    label: 'Follow-up 1',  desc: 'Pitched, no follow-up' },
    { key: 'followUp2',    label: 'Follow-up 2',  desc: 'Followed up once' },
    { key: 'followUp3',    label: 'Last Follow-up', desc: 'Followed up twice' },
  ];
  const isFollowUpType = sendType !== 'initialPitch';

  useEffect(() => {
    // ophalai.com accounts only — lead outreach must never send from a
    // client's own connected account (see GET /api/leads/accounts).
    api.get('/leads/accounts').then(r => {
      const accounts = r.data.accounts || [];
      setGoogleAccounts(accounts);
      if (accounts.length) setAccountEmail(accounts[0].email);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/leads/eligible/${sendType}`).then(res => {
      if (cancelled) return;
      setEligible(res.data);
      setCount(Math.min(10, res.data.length));
    }).catch(() => { if (!cancelled) toast('Could not load eligible leads', 'error'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [sendType]);

  const rangeFrom = rowFrom ? parseInt(rowFrom) : null;
  const rangeTo = rowTo ? parseInt(rowTo) : null;
  const matchesFollowUpFilter = (l) => {
    if (!isFollowUpType || !followUpFilterEmail) return true;
    // Leads with no recorded senderEmail (legacy, or account probed at send
    // time) must NOT be guessed into a default account — that guess can be
    // wrong and silently mixes another account's volume into this one.
    if (!l.senderEmail) return false;
    return l.senderEmail === followUpFilterEmail;
  };
  const rangeFilteredEligible = eligible.filter(l => {
    if (rangeFrom && l.sheetRowNum < rangeFrom) return false;
    if (rangeTo && l.sheetRowNum > rangeTo) return false;
    if (!matchesFollowUpFilter(l)) return false;
    return true;
  });

  useEffect(() => {
    const from = rowFrom ? parseInt(rowFrom) : null;
    const to = rowTo ? parseInt(rowTo) : null;
    const filtered = eligible.filter(l => {
      if (from && l.sheetRowNum < from) return false;
      if (to && l.sheetRowNum > to) return false;
      if (!matchesFollowUpFilter(l)) return false;
      return true;
    });
    if (!from && !to && !(isFollowUpType && followUpFilterEmail)) {
      setCount(Math.min(10, filtered.length));
      return;
    }
    setCount(filtered.length);
  }, [rowFrom, rowTo, eligible, followUpFilterEmail, isFollowUpType]);

  // Reset the account filter when switching send type so a stale filter doesn't hide results
  useEffect(() => {
    setFollowUpFilterEmail('');
  }, [sendType]);

  const toSend = rangeFilteredEligible.slice(0, count);

  const handleSend = async () => {
    const leadIds = toSend.map(l => l.id);
    if (!leadIds.length) return;
    try {
      const res = await api.post('/leads/send', { leadIds, sendType, subject: !isFollowUpType ? subject : null, accountEmail: !isFollowUpType ? accountEmail : null });
      const typeLabel = TYPES.find(t => t.key === sendType)?.label || 'Outreach';
      lastJobRef.current = null;
      addJob(res.data.jobId, `Leads — ${typeLabel}`, leadIds.length, 'lead');
      setJobId(res.data.jobId);
    } catch (e) {
      toast(e.response?.data?.error || 'Could not start send', 'error');
    }
  };

  const handleStop = async () => {
    if (jobId) await ctxCancelJob(jobId);
  };

  const done = !!job?.done;
  // Closing mid-send minimizes to the floating card; closing after it's done
  // clears the card too, same as podcast outreach's Done button.
  const handleClose = () => {
    if (jobId && done) { dismissJob(jobId); onSent(); }
    else if (jobId) onSent();
    onClose();
  };

  return (
    <div className="modal-overlay">
      <div className="modal modal-lg">
        <div className="modal-header">
          <h2>📤 Send Outreach</h2>
          <button className="btn btn-ghost btn-icon" onClick={handleClose} title={jobId && !done ? 'Minimize — keeps sending in the background' : 'Close'}>✕</button>
        </div>

        {jobId ? (
          <>
            <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
              <div style={{ flex: 1, background: 'var(--green-soft)', border: '1px solid rgba(34,197,94,0.2)', borderRadius: 'var(--radius-sm)', padding: 10, textAlign: 'center' }}>
                <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--green)' }}>{job?.sent ?? 0}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Sent</div>
              </div>
              <div style={{ flex: 1, background: 'var(--bg-elevated)', borderRadius: 'var(--radius-sm)', padding: 10, textAlign: 'center' }}>
                <div style={{ fontSize: 24, fontWeight: 700 }}>{Math.max(0, (job?.total ?? toSend.length) - (job?.sent ?? 0) - (job?.failed ?? 0))}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Remaining</div>
              </div>
              {(job?.failed ?? 0) > 0 && (
                <div style={{ flex: 1, background: 'var(--red-soft)', borderRadius: 'var(--radius-sm)', padding: 10, textAlign: 'center' }}>
                  <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--red)' }}>{job.failed}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Failed</div>
                </div>
              )}
            </div>
            <LogBox logs={job?.rawLogs || []} />
            <div className="flex-center gap-2" style={{ marginTop: 16 }}>
              {!done ? (
                <button className="btn btn-danger" onClick={handleStop}>Stop</button>
              ) : (
                <button className="btn btn-primary" onClick={handleClose}>Done</button>
              )}
            </div>
          </>
        ) : (
          <>
            {!isFollowUpType && (
              <div className="form-group" style={{ marginBottom: 16 }}>
                <label>Email Subject</label>
                <input value={subject} onChange={e => setSubject(e.target.value)} />
              </div>
            )}
            {googleAccounts.length > 1 && !isFollowUpType && (
              <div className="form-group" style={{ marginBottom: 16 }}>
                <label>Send from</label>
                <select value={accountEmail} onChange={e => setAccountEmail(e.target.value)} style={{ width: 'auto' }}>
                  {googleAccounts.map(a => <option key={a.email} value={a.email}>{a.email}</option>)}
                </select>
              </div>
            )}
            {googleAccounts.length > 1 && isFollowUpType && (
              <div className="form-group" style={{ marginBottom: 16 }}>
                <label>Filter by sending account</label>
                <select value={followUpFilterEmail} onChange={e => setFollowUpFilterEmail(e.target.value)} style={{ width: 'auto' }}>
                  <option value="">All accounts</option>
                  {googleAccounts.map(a => (
                    <option key={a.email} value={a.email}>{a.email}</option>
                  ))}
                </select>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                  Follow-ups always send from whichever account sent the original outreach — pick one here to only show (and send) the follow-ups tied to it, so you can spread sends across accounts.
                </div>
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
              {TYPES.map(t => (
                <button key={t.key} onClick={() => setSendType(t.key)} style={{
                  padding: '10px 16px', borderRadius: 'var(--radius-sm)', cursor: 'pointer',
                  border: `1px solid ${sendType === t.key ? 'var(--accent)' : 'var(--border)'}`,
                  background: sendType === t.key ? 'var(--accent-soft)' : 'var(--bg-elevated)', textAlign: 'left',
                }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: sendType === t.key ? 'var(--accent)' : 'var(--text-primary)' }}>{t.label}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{t.desc}</div>
                </button>
              ))}
            </div>
            {loading ? <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>Loading...</div> : (
              <>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-end', gap: 24, flexWrap: 'wrap' }}>
                    <div>
                      <label>How many to send? <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>({rangeFilteredEligible.length} available)</span></label>
                      <input type="number" value={count} min={1} max={rangeFilteredEligible.length || 1}
                        onChange={e => setCount(Math.max(1, Math.min(rangeFilteredEligible.length, parseInt(e.target.value) || 1)))}
                        style={{ width: 80, marginTop: 8 }} />
                    </div>
                    <div>
                      <label>Row Range <span style={{ color: 'var(--text-muted)', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(optional)</span></label>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
                        <input type="number" value={rowFrom} onChange={e => setRowFrom(e.target.value)} placeholder="From row" style={{ width: 90 }} min={1} />
                        <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>to</span>
                        <input type="number" value={rowTo} onChange={e => setRowTo(e.target.value)} placeholder="To row" style={{ width: 90 }} min={1} />
                      </div>
                    </div>
                  </div>
                </div>
                {toSend.length === 0 ? (
                  <div style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 16 }}>No leads ready for this type.</div>
                ) : (
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ marginBottom: 8, display: 'block' }}>Will send to:</label>
                    <div style={{ maxHeight: 200, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {toSend.map((l, i) => (
                        <div key={l.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 10px', background: 'var(--bg-elevated)', borderRadius: 'var(--radius-sm)', fontSize: 13 }}>
                          <span style={{ color: 'var(--text-muted)', width: 20, fontSize: 11 }}>{i + 1}</span>
                          <div style={{ flex: 1 }}><div style={{ fontWeight: 500 }}>{l.fullName}</div><div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{l.email}</div></div>
                          {isFollowUpType && <span style={{ fontSize: 11, color: l.senderEmail ? 'var(--text-muted)' : 'var(--amber)' }}>{l.senderEmail || 'Unknown — resolved at send'}</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex-center gap-2" style={{ justifyContent: 'flex-end' }}>
                  <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
                  <button className="btn btn-primary" disabled={toSend.length === 0} onClick={handleSend}>
                    🚀 Send {toSend.length} {TYPES.find(t => t.key === sendType)?.label}
                  </button>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ─── Main Leads Page ────────────────────────────────────────────────────────
export default function Leads() {
  const toast = useToast();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [scanModal, setScanModal] = useState(false);
  const [sendModal, setSendModal] = useState(false);
  const [backfillModal, setBackfillModal] = useState(null); // null | 'threads' | 'followups' | 'status'
  const [editLead, setEditLead] = useState(null);
  const [syncing, setSyncing] = useState(false);

  // Lightweight local refresh — used after actions that already synced
  // server-side (scan, send), so we're just re-reading the local DB.
  const refresh = useCallback(() => {
    return api.get('/leads').then(r => setLeads(r.data)).catch(() => toast('Could not load leads', 'error'));
  }, [toast]);

  // Full sync with the master Google Sheet — the sheet is the shared source
  // of truth across everyone's local PitchFlow instance, so this is what
  // picks up leads/status/sends made from a teammate's machine.
  const sync = useCallback(() => {
    setSyncing(true);
    return api.post('/leads/sync').then(r => setLeads(r.data.leads))
      .catch(() => toast('Could not sync with Leads sheet', 'error'))
      .finally(() => setSyncing(false));
  }, [toast]);

  useEffect(() => {
    setLoading(true);
    sync().finally(() => setLoading(false));
  }, [sync]);

  const handleManualSync = async () => {
    await sync();
    toast('Synced ✓', 'success');
  };

  const handleStatusChange = async (id, status) => {
    try {
      const res = await api.patch(`/leads/${id}`, { status });
      setLeads(prev => prev.map(l => l.id === id ? res.data : l));
    } catch (e) { toast('Error updating', 'error'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this lead?')) return;
    try {
      await api.delete(`/leads/${id}`);
      setLeads(prev => prev.filter(l => l.id !== id));
      toast('Deleted', 'success');
    } catch (e) { toast('Could not delete', 'error'); }
  };

  const filtered = leads
    .filter(l => filter === 'all' || l.status === filter)
    .filter(l => !search ||
      l.fullName.toLowerCase().includes(search.toLowerCase()) ||
      (l.podcastName || '').toLowerCase().includes(search.toLowerCase()) ||
      (l.email || '').toLowerCase().includes(search.toLowerCase())
    );

  const counts = {};
  STATUSES.forEach(s => { counts[s.key] = s.key === 'all' ? leads.length : leads.filter(l => l.status === s.key).length; });

  return (
    <div className="page-content">
      <div className="page-header flex-between">
        <div>
          <h1>Leads</h1>
          <p>Past podcast guests found as potential new PR clients — track, edit, and pitch them</p>
        </div>
        <div className="flex-center gap-2">
          <button className="btn btn-secondary" onClick={handleManualSync} disabled={syncing}>
            {syncing ? '↻ Syncing...' : '↻ Sync'}
          </button>
          <button className="btn btn-secondary" onClick={() => setBackfillModal('threads')}>Find Missing Thread IDs</button>
          <button className="btn btn-secondary" onClick={() => setBackfillModal('followups')}>Backfill Follow-Up Dates</button>
          <button className="btn btn-secondary" onClick={() => setBackfillModal('status')}>Review Replies</button>
          <button className="btn btn-secondary" onClick={() => setScanModal(true)}>Scan for New Leads</button>
          <button className="btn btn-primary" onClick={() => setSendModal(true)}>📤 Send Outreach</button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
        {STATUSES.filter(s => s.key === 'all' || counts[s.key] > 0).map(s => (
          <button key={s.key} className={`btn btn-sm ${filter === s.key ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setFilter(s.key)}>
            {s.label} ({counts[s.key]})
          </button>
        ))}
      </div>

      <div style={{ marginBottom: 16 }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, podcast, or email..." style={{ maxWidth: 360 }} />
      </div>

      {loading ? (
        <div className="empty-state"><p>Loading...</p></div>
      ) : filtered.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">🧲</div>
          <h3>{search ? 'No results' : 'No leads yet'}</h3>
          <p>{search ? 'Try a different search' : 'Scan a podcast to find your first leads'}</p>
        </div>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Guest</th>
                <th>Source Podcast</th>
                <th>Category / Topics</th>
                <th>Email</th>
                <th>Status</th>
                <th>Pitched</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(lead => (
                <tr key={lead.id}>
                  <td>
                    <div style={{ fontWeight: 500, fontSize: 13 }}>{lead.fullName}</div>
                    {lead.socials && <div style={{ fontSize: 11, color: 'var(--text-muted)', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{lead.socials}</div>}
                  </td>
                  <td>
                    <div style={{ fontSize: 13 }}>{lead.podcastName || '—'}</div>
                    {lead.hostName && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Host: {lead.hostName}</div>}
                    {lead.episodeLink && <a href={lead.episodeLink} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: 'var(--accent)' }}>↗ Episode</a>}
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--text-secondary)', maxWidth: 220 }}>
                    <div>{lead.category || '—'}</div>
                    {lead.topics && <div style={{ color: 'var(--text-muted)', marginTop: 2 }}>{lead.topics}</div>}
                  </td>
                  <td style={{ fontSize: 12 }}>
                    {lead.email ? lead.email : <span style={{ color: 'var(--amber)' }}>⚠ Missing</span>}
                  </td>
                  <td>
                    <select value={lead.status} onChange={e => handleStatusChange(lead.id, e.target.value)} style={{ width: 'auto', padding: '4px 8px', fontSize: 12 }}>
                      {STATUSES.filter(s => s.key !== 'all').map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                    </select>
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{lead.initialPitchDate || '—'}</td>
                  <td>
                    <div className="flex-center gap-1">
                      <button className="btn btn-sm btn-secondary" onClick={() => setEditLead(lead)}>Edit</button>
                      <button className="btn btn-sm btn-danger" onClick={() => handleDelete(lead.id)}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {scanModal && <ScanModal onClose={() => setScanModal(false)} onDone={() => { setScanModal(false); refresh(); }} onRefresh={refresh} />}
      {sendModal && <SendLeadsModal onClose={() => setSendModal(false)} onSent={refresh} />}
      {backfillModal && (
        <BackfillModal
          {...BACKFILL_CONFIGS[backfillModal]}
          onClose={() => setBackfillModal(null)}
          onDone={refresh}
        />
      )}
      {editLead && (
        <EditLeadModal
          lead={editLead}
          onClose={() => setEditLead(null)}
          onSave={(updated) => { setLeads(prev => prev.map(l => l.id === updated.id ? updated : l)); setEditLead(null); }}
        />
      )}
    </div>
  );
}
